import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertCircle, Building2, Check, ChevronDown, Copy, Layers, ListPlus,
    Loader2, Mail, Plus, Save, Search, Sparkles, Tags, Trash2, UserSearch, X,
} from "lucide-react";
import {
    ClientProfile, ClientTypingResponse, ClientTypology, EstablishmentPick,
    MAX_SELECTION, MIN_SELECTION, StatBlock, analyzeClientTypes, createTypology,
    deleteTypology, listTypologies, searchEstablishmentsForTyping,
} from "../../interfaces/clientProfiles";
import { getDefaultProspectionList, getProspectionList, getProspectionLists } from "../../interfaces/prospectionLists";
import { IProspectionListSummary } from "../../interfaces/prospectionList";
import { useProspectionListCounts, formatListCount } from "../../hooks/useProspectionListCounts";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Input } from "../../components/ui/Input";
import { Card, StatCard } from "../../components/ui/Card";

/**
 * Client typing: pick a set of establishments — your clients, or a prospection list — and read
 * back what that portfolio is actually made of.
 *
 * Two halves, and they are not equally trustworthy on purpose. Every percentage on this page
 * is counted server-side over the selection; the assistant only names the groups and writes
 * the search prompt. So when Groq is down the page still renders its distributions (the API
 * answers 200 with `aiError`), and the profile cards simply don't appear.
 */

const STORAGE_KEY = "clientTypes_selection";

interface SelectedEstablishment {
    _id: string;
    name: string;
    city?: string | null;
}

// ─── Selection persistence ────────────────────────────────────────────────────
// Kept across navigations because the main action on a result — "chercher dans la base" —
// leaves the page, and rebuilding a 40-restaurant selection by hand afterwards is punishing.

const loadSelection = (): SelectedEstablishment[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return Array.isArray(parsed) ? parsed.filter((e: any) => e?._id && e?.name) : [];
    } catch {
        return [];
    }
};

const saveSelection = (selection: SelectedEstablishment[]) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch {
        /* a full quota must not break the page */
    }
};

// ─── Establishment picker ─────────────────────────────────────────────────────

function EstablishmentSearch({
    onAdd,
    selectedIds,
    disabled,
}: {
    onAdd: (establishment: SelectedEstablishment) => void;
    selectedIds: Set<string>;
    disabled: boolean;
}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<EstablishmentPick[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const text = query.trim();
        if (text.length < 2) {
            setResults([]);
            // Also clears the spinner a longer query had turned on: its request was cancelled
            // by this effect's own cleanup, so nothing else is left to switch it back off.
            setLoading(false);
            return;
        }
        // Debounced, and the in-flight response of a previous keystroke is dropped rather
        // than allowed to overwrite the newer one.
        let cancelled = false;
        setLoading(true);
        const timer = setTimeout(() => {
            searchEstablishmentsForTyping(text, 12)
                .then(found => { if (!cancelled) { setResults(found); setOpen(true); } })
                .catch(() => { if (!cancelled) setResults([]); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [query]);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [open]);

    return (
        <div ref={containerRef} className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint" />
            <Input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onFocus={() => results.length > 0 && setOpen(true)}
                placeholder="Rechercher un établissement par nom ou par ville…"
                disabled={disabled}
                className="pl-9 pr-9"
            />
            {loading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint motion-safe:animate-spin" />
            )}

            {open && results.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 z-20 max-h-72 overflow-y-auto rounded-2xl border border-border bg-surface shadow-lg p-1">
                    {results.map(result => {
                        const already = selectedIds.has(result._id);
                        return (
                            <button
                                key={result._id}
                                type="button"
                                disabled={already || disabled}
                                onClick={() => {
                                    onAdd({ _id: result._id, name: result.name, city: result.city });
                                    setQuery("");
                                    setResults([]);
                                    setOpen(false);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-surface-sunken disabled:opacity-40 disabled:hover:bg-transparent transition-colors duration-150"
                            >
                                <Building2 className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                                <span className="text-xs font-medium text-ink truncate">{result.name}</span>
                                <span className="text-xs text-ink-muted truncate">
                                    {[result.city, result.postcode].filter(Boolean).join(" · ")}
                                </span>
                                <span className="ml-auto text-[10px] text-ink-faint truncate max-w-[35%]">
                                    {[...result.categories, ...result.subCategories].slice(0, 2).join(" · ")}
                                </span>
                                {already && <Check className="w-3.5 h-3.5 text-success shrink-0" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Prospection list import ──────────────────────────────────────────────────

function ListImporter({
    onImport,
    disabled,
}: {
    onImport: (establishments: SelectedEstablishment[]) => void;
    disabled: boolean;
}) {
    const [lists, setLists] = useState<IProspectionListSummary[]>([]);
    const [open, setOpen] = useState(false);
    const counts = useProspectionListCounts(lists.map(l => l._id));
    const [importingId, setImportingId] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        getProspectionLists().then(setLists).catch(() => setLists([]));
    }, []);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [open]);

    const importList = async (id: string) => {
        setImportingId(id);
        try {
            const list = await getProspectionList(id);
            onImport(
                (list.leads || [])
                    .map(lead => lead.establishment)
                    .filter((e): e is NonNullable<typeof e> => !!e?._id)
                    .map(e => ({ _id: String(e._id), name: e.name, city: e.address?.city }))
            );
            setOpen(false);
        } catch {
            /* the list simply doesn't get added; the picker stays usable */
        } finally {
            setImportingId(null);
        }
    };

    if (lists.length === 0) return null;

    return (
        <div ref={containerRef} className="relative">
            <Button variant="secondary" onClick={() => setOpen(o => !o)} disabled={disabled}>
                <ListPlus className="w-4 h-4" />
                Depuis une liste
                <ChevronDown className="w-3.5 h-3.5" />
            </Button>

            {open && (
                <div className="absolute right-0 top-full mt-1 z-20 w-72 max-h-72 overflow-y-auto rounded-2xl border border-border bg-surface shadow-lg p-1">
                    {lists.map(list => (
                        <button
                            key={list._id}
                            type="button"
                            onClick={() => importList(list._id)}
                            disabled={!!importingId}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-surface-sunken disabled:opacity-40 transition-colors duration-150"
                        >
                            <span className="text-xs font-medium text-ink truncate flex-1">{list.name}</span>
                            <span className="text-[10px] text-ink-faint shrink-0">
                                {formatListCount(counts[list._id])}
                            </span>
                            {importingId === list._id && (
                                <Loader2 className="w-3.5 h-3.5 text-ink-faint motion-safe:animate-spin" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Distribution block ───────────────────────────────────────────────────────

function DistributionCard({ block, total }: { block: StatBlock; total: number }) {
    const [expanded, setExpanded] = useState(false);
    const items = expanded ? block.items.slice(0, 20) : block.items.slice(0, 5);
    const max = block.items[0]?.pct || 100;

    return (
        <div className="rounded-2xl border border-border bg-surface p-4">
            <div className="flex items-baseline justify-between gap-2 mb-3">
                <p className="text-xs font-semibold text-ink">{block.label}</p>
                <span className="text-[10px] text-ink-faint shrink-0">
                    {block.coverage}/{total} renseignés
                </span>
            </div>

            <div className="space-y-2">
                {items.map((item, i) => (
                    <div key={item.label}>
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-xs text-ink truncate" title={item.label}>{item.label}</span>
                            <span className="text-xs font-medium text-ink-muted shrink-0 tabular-nums">
                                {item.pct.toLocaleString("fr-FR")} % · {item.count}
                            </span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-surface-sunken overflow-hidden">
                            <div
                                className={`h-full rounded-full ${i === 0 ? "bg-accent" : "bg-ink/25"}`}
                                style={{ width: `${Math.max(2, (item.pct / max) * 100)}%` }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {block.items.length > 5 && (
                <button
                    onClick={() => setExpanded(e => !e)}
                    className="mt-3 text-[10px] font-medium uppercase tracking-wide text-ink-faint hover:text-ink transition-colors duration-150"
                >
                    {expanded ? "Réduire" : `Voir les ${Math.min(block.items.length, 20)} valeurs`}
                </button>
            )}
        </div>
    );
}

// ─── Profile card ─────────────────────────────────────────────────────────────

function ProfileCard({ profile, rank, onSaved }: {
    profile: ClientProfile;
    rank: number;
    onSaved: () => void;
}) {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [showMembers, setShowMembers] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState("");

    // Saving keeps the *description* — label, traits, prompt — plus the members as examples.
    // The share and the counts are deliberately left behind: they describe this analysis, and
    // a typology is meant to be re-run against a database that has moved on since.
    const saveAsTypology = async () => {
        setSaving(true);
        setSaveError("");
        try {
            await createTypology({
                name: profile.label,
                description: profile.description,
                searchPrompt: profile.searchPrompt,
                traits: profile.defining,
                referenceEstablishmentIds: profile.members.map(m => m.id),
                origin: `Profil « ${profile.label} » — analyse du ${new Date().toLocaleDateString("fr-FR")}`,
            });
            setSaved(true);
            onSaved();
        } catch (err: any) {
            setSaveError(
                err?.response?.data?.error === "SEARCH_PROMPT_REQUIRED"
                    ? "Ce profil n'a pas de prompt de recherche : impossible de l'enregistrer."
                    : "Enregistrement impossible."
            );
        } finally {
            setSaving(false);
        }
    };

    const copyPrompt = async () => {
        try {
            await navigator.clipboard.writeText(profile.searchPrompt);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard blocked — the prompt is selectable in the box anyway */
        }
    };

    return (
        <Card className="space-y-4">
            <div className="flex items-start gap-4">
                <div className="shrink-0 text-center">
                    <p className="font-sans text-3xl font-semibold tracking-tight text-ink tabular-nums">
                        {profile.pct.toLocaleString("fr-FR")} %
                    </p>
                    <p className="text-[10px] text-ink-faint">{profile.count} établissements</p>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <Badge tone={rank === 0 ? "accent" : "neutral"}>Profil {rank + 1}</Badge>
                    </div>
                    <h3 className="text-base font-semibold text-ink">{profile.label}</h3>
                    {profile.description && (
                        <p className="text-sm text-ink-muted mt-1 leading-relaxed">{profile.description}</p>
                    )}
                </div>
            </div>

            {profile.defining.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {profile.defining.map(trait => (
                        <span
                            key={trait}
                            className="inline-flex items-center rounded-full border border-border bg-surface-sunken px-2.5 py-1 text-xs text-ink-muted"
                        >
                            {trait}
                        </span>
                    ))}
                </div>
            )}

            {profile.facts.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 rounded-xl bg-surface-sunken p-3">
                    {profile.facts.map(fact => (
                        <div key={fact.label} className="min-w-0">
                            <p className="text-[10px] uppercase tracking-wide text-ink-faint truncate">{fact.label}</p>
                            <p className="text-xs font-medium text-ink truncate" title={fact.value}>{fact.value}</p>
                        </div>
                    ))}
                </div>
            )}

            {profile.searchPrompt && (
                <div className="rounded-xl border border-border p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint mb-2 flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-accent" />
                        Prompt pour en trouver d'autres
                    </p>
                    <p className="text-sm text-ink leading-relaxed">{profile.searchPrompt}</p>
                    <div className="flex items-center gap-2 mt-3">
                        <Button
                            size="sm"
                            onClick={() => navigate(`/database?aiPrompt=${encodeURIComponent(profile.searchPrompt)}`)}
                        >
                            <Search className="w-3.5 h-3.5" />
                            Chercher dans la base
                        </Button>
                        <Button size="sm" variant="secondary" onClick={copyPrompt}>
                            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            {copied ? "Copié" : "Copier"}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={saveAsTypology} disabled={saving || saved}>
                            {saving
                                ? <Loader2 className="w-3.5 h-3.5 motion-safe:animate-spin" />
                                : saved ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
                            {saved ? "Typologie enregistrée" : "Enregistrer comme typologie"}
                        </Button>
                    </div>
                    {saveError && (
                        <p className="flex items-center gap-1.5 text-xs text-danger mt-2">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            {saveError}
                        </p>
                    )}
                    {saved && (
                        <p className="text-xs text-ink-faint mt-2">
                            Disponible dans la barre de recherche du mail hebdo (back-office).
                        </p>
                    )}
                </div>
            )}

            <div>
                <button
                    onClick={() => setShowMembers(s => !s)}
                    className="text-[10px] font-medium uppercase tracking-wide text-ink-faint hover:text-ink transition-colors duration-150"
                >
                    {showMembers ? "Masquer les établissements" : `Voir les ${profile.count} établissements`}
                </button>
                {showMembers && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {profile.members.map(member => (
                            <a
                                key={member.id}
                                href={`/restaurants/${member.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-ink-muted hover:border-border-strong hover:text-ink transition-colors duration-150"
                            >
                                {member.name}
                                {member.city && <span className="text-ink-faint">· {member.city}</span>}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
}

// ─── Saved typologies ─────────────────────────────────────────────────────────

/**
 * The types saved from this page, and where new ones are written by hand.
 *
 * They live here rather than in the back-office because this is the page where you look at
 * clients and recognise a pattern. What consumes them is elsewhere: the weekly brief's editor
 * runs `searchPrompt` through the same assistant as the Database page's filter bar, so a type
 * finds exactly what its own words would find there.
 */
function TypologyManager({ typologies, loading, onChanged }: {
    typologies: ClientTypology[];
    loading: boolean;
    onChanged: () => void;
}) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [prompt, setPrompt] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const create = async () => {
        setBusy(true);
        setError("");
        try {
            await createTypology({
                name: name.trim(),
                description: description.trim() || undefined,
                searchPrompt: prompt.trim(),
                origin: "Saisie manuelle",
            });
            setName("");
            setPrompt("");
            setDescription("");
            setOpen(false);
            onChanged();
        } catch (err: any) {
            const code = err?.response?.data?.error;
            setError(
                code === "NAME_REQUIRED" ? "Donnez un nom à cette typologie."
                    : code === "SEARCH_PROMPT_REQUIRED" ? "Décrivez le type de client recherché."
                    : "Enregistrement impossible."
            );
        } finally {
            setBusy(false);
        }
    };

    const remove = async (typology: ClientTypology) => {
        if (!window.confirm(`Supprimer la typologie « ${typology.name} » ?`)) return;
        await deleteTypology(typology._id);
        onChanged();
    };

    return (
        <Card className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-base font-semibold text-ink flex items-center gap-2">
                        <Tags className="w-4 h-4 text-accent" />
                        Typologies enregistrées
                    </h2>
                    <p className="text-xs text-ink-muted mt-0.5">
                        Réutilisables dans l'éditeur du mail hebdo pour trouver des établissements du même type
                    </p>
                </div>
                <Button size="sm" variant="secondary" onClick={() => setOpen(o => !o)}>
                    {open ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    {open ? "Annuler" : "Nouvelle typologie"}
                </Button>
            </div>

            {open && (
                <div className="space-y-3 rounded-xl border border-border p-3">
                    <Input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Nom : ex. « Boulangerie de bourg »"
                    />
                    <textarea
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        rows={3}
                        placeholder="Décrivez le client en une phrase : ex. « boulangeries indépendantes dans des communes de moins de 10 000 habitants, dirigeant de plus de 45 ans, note Google supérieure à 4 »"
                        className="w-full rounded-lg border border-border bg-surface p-3 font-sans text-sm text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    />
                    <p className="text-[11px] text-ink-faint">
                        Cette phrase est lue par l'assistant, exactement comme dans la barre de recherche IA de la
                        base : plus elle est précise en zone, activité et profil de dirigeant, plus la sélection l'est.
                    </p>
                    <Input
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Note interne (facultatif)"
                    />
                    {error && (
                        <p className="flex items-center gap-1.5 text-xs text-danger">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            {error}
                        </p>
                    )}
                    <Button size="sm" onClick={create} disabled={busy || !name.trim() || !prompt.trim()}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 motion-safe:animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Enregistrer
                    </Button>
                </div>
            )}

            {loading ? (
                <p className="flex items-center gap-2 text-xs text-ink-faint">
                    <Loader2 className="w-3.5 h-3.5 motion-safe:animate-spin" /> Chargement…
                </p>
            ) : typologies.length === 0 ? (
                <p className="text-xs text-ink-faint">
                    Aucune typologie pour le moment. Lancez une analyse puis enregistrez un profil, ou créez-en une à la main.
                </p>
            ) : (
                <div className="space-y-2">
                    {typologies.map(typology => (
                        <div key={typology._id} className="rounded-xl border border-border p-3">
                            <div className="flex items-start gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-sm font-semibold text-ink">{typology.name}</p>
                                        {typology.useCount > 0 && (
                                            <Badge tone="neutral">
                                                {typology.useCount} recherche{typology.useCount > 1 ? "s" : ""}
                                            </Badge>
                                        )}
                                    </div>
                                    {typology.description && (
                                        <p className="text-xs text-ink-muted mt-0.5">{typology.description}</p>
                                    )}
                                    <p className="text-xs text-ink mt-1.5 leading-relaxed">{typology.searchPrompt}</p>
                                    {typology.traits.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            {typology.traits.map(trait => (
                                                <span
                                                    key={trait}
                                                    className="inline-flex items-center rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-[11px] text-ink-muted"
                                                >
                                                    {trait}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {typology.origin && (
                                        <p className="text-[10px] text-ink-faint mt-2">{typology.origin}</p>
                                    )}
                                </div>
                                <button
                                    onClick={() => remove(typology)}
                                    className="shrink-0 text-ink-faint hover:text-danger transition-colors duration-150"
                                    aria-label={`Supprimer ${typology.name}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                    <p className="flex items-center gap-1.5 text-[11px] text-ink-faint pt-1">
                        <Mail className="w-3 h-3 shrink-0" />
                        Utilisables depuis « Mails hebdomadaires » du back-office, dans l'aperçu d'un envoi.
                    </p>
                </div>
            )}
        </Card>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const AI_ERRORS: Record<string, string> = {
    AI_UNAVAILABLE: "L'assistant n'est pas configuré sur ce serveur : seules les statistiques sont affichées.",
    AI_CALL_FAILED: "L'assistant n'a pas répondu. Les statistiques sont à jour, relancez l'analyse pour les profils.",
    AI_BAD_OUTPUT: "L'assistant n'a pas produit de profils exploitables. Réessayez, ou élargissez la sélection.",
    AI_RATE_LIMITED: "L'assistant a atteint son quota de la minute. Les statistiques sont là, relancez l'analyse dans une minute.",
    AI_TOO_LARGE: "Sélection trop volumineuse pour l'assistant : retirez quelques établissements et relancez. Les statistiques ci-dessous couvrent bien toute la sélection.",
};

export default function ClientTypes() {
    const navigate = useNavigate();
    const [selection, setSelection] = useState<SelectedEstablishment[]>(loadSelection);
    const [result, setResult] = useState<ClientTypingResponse | null>(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [error, setError] = useState("");
    /** Selection the displayed result was computed on, to flag it as stale after an edit. */
    const [analyzedKey, setAnalyzedKey] = useState<string | null>(null);
    /** Whether the user's "Mes clients" default list has any establishment yet. */
    const [defaultListEmpty, setDefaultListEmpty] = useState<boolean | null>(null);
    const [typologies, setTypologies] = useState<ClientTypology[]>([]);
    const [typologiesLoading, setTypologiesLoading] = useState(true);

    useEffect(() => { saveSelection(selection); }, [selection]);

    const reloadTypologies = useCallback(() => {
        setTypologiesLoading(true);
        listTypologies()
            .then(setTypologies)
            .catch(() => { /* the section shows an empty list rather than breaking the page */ })
            .finally(() => setTypologiesLoading(false));
    }, []);

    useEffect(() => { reloadTypologies(); }, [reloadTypologies]);

    // "Mes clients" is the canonical source for this page: pre-fill the selection with it on
    // first load, and remember whether it's empty so the empty-state can point there instead
    // of showing the generic "search or import" hint.
    useEffect(() => {
        let cancelled = false;
        getDefaultProspectionList()
            .then(list => {
                if (cancelled) return;
                const establishments: SelectedEstablishment[] = (list.leads || [])
                    .map(lead => lead.establishment)
                    .filter((e): e is NonNullable<typeof e> => !!e?._id)
                    .map(e => ({ _id: String(e._id), name: e.name, city: e.address?.city }));
                setDefaultListEmpty(establishments.length === 0);
                if (establishments.length > 0) {
                    setSelection(prev => (prev.length === 0 ? establishments.slice(0, MAX_SELECTION) : prev));
                }
            })
            .catch(() => {
                /* default list unavailable — fall back to the generic empty state */
            });
        return () => { cancelled = true; };
    }, []);

    const selectedIds = useMemo(() => new Set(selection.map(e => e._id)), [selection]);
    const selectionKey = useMemo(() => selection.map(e => e._id).sort().join(","), [selection]);
    const stale = !!result && analyzedKey !== null && analyzedKey !== selectionKey;

    const addOne = useCallback((establishment: SelectedEstablishment) => {
        setSelection(prev =>
            prev.some(e => e._id === establishment._id) || prev.length >= MAX_SELECTION
                ? prev
                : [...prev, establishment]
        );
    }, []);

    const addMany = useCallback((establishments: SelectedEstablishment[]) => {
        setSelection(prev => {
            const seen = new Set(prev.map(e => e._id));
            const merged = [...prev];
            for (const establishment of establishments) {
                if (merged.length >= MAX_SELECTION) break;
                if (seen.has(establishment._id)) continue;
                seen.add(establishment._id);
                merged.push(establishment);
            }
            return merged;
        });
    }, []);

    const removeOne = (id: string) => setSelection(prev => prev.filter(e => e._id !== id));

    const analyze = async () => {
        setAnalyzing(true);
        setError("");
        try {
            setResult(await analyzeClientTypes(selection.map(e => e._id)));
            setAnalyzedKey(selectionKey);
        } catch (err: any) {
            const code = err?.response?.data?.error;
            if (code === "TOO_FEW_ESTABLISHMENTS") setError(`Sélectionnez au moins ${MIN_SELECTION} établissements.`);
            else if (code === "TOO_MANY_ESTABLISHMENTS") {
                setError(`Maximum ${err?.response?.data?.max ?? MAX_SELECTION} établissements par analyse.`);
            }
            else if (code === "TOO_MANY_REQUESTS") setError("Trop d'analyses d'affilée. Patientez une minute.");
            else setError("L'analyse a échoué. Réessayez dans un instant.");
        } finally {
            setAnalyzing(false);
        }
    };

    const atCapacity = selection.length >= MAX_SELECTION;

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-6 space-y-6">

                {/* ── Header ── */}
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-ink flex items-center justify-center shrink-0">
                        <UserSearch className="w-5 h-5 text-ink-inverse" />
                    </div>
                    <div className="flex-1">
                        <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">Intelligence clients</h1>
                        <p className="text-sm text-ink-muted">
                            Sélectionnez vos clients : DuckFlow en déduit vos profils types et le prompt pour en trouver d'autres
                        </p>
                    </div>
                </div>

                {/* ── Selection ── */}
                <Card className="space-y-4">
                    <div className="flex items-center gap-2 flex-wrap">
                        <EstablishmentSearch onAdd={addOne} selectedIds={selectedIds} disabled={atCapacity} />
                        <ListImporter onImport={addMany} disabled={atCapacity} />
                    </div>

                    <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-ink-muted">
                            <span className="font-semibold text-ink">{selection.length}</span> / {MAX_SELECTION} sélectionnés
                            {atCapacity && <span className="text-warning"> · maximum atteint</span>}
                            {selection.length > 0 && selection.length < MIN_SELECTION && (
                                <span className="text-ink-faint"> · {MIN_SELECTION} minimum pour analyser</span>
                            )}
                        </p>
                        {selection.length > 0 && (
                            <button
                                onClick={() => { setSelection([]); setResult(null); }}
                                className="flex items-center gap-1 text-xs text-ink-faint hover:text-danger transition-colors duration-150"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                Tout effacer
                            </button>
                        )}
                    </div>

                    {selection.length === 0 && defaultListEmpty ? (
                        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-8">
                            <UserSearch className="w-8 h-8 text-ink-faint mb-2" />
                            <p className="text-sm font-semibold text-ink mb-0.5">Vous n'avez pas encore identifié vos clients</p>
                            <p className="text-xs text-ink-muted mb-3">
                                Ajoutez des établissements à votre liste "Mes clients" pour lancer une analyse
                            </p>
                            <Button size="sm" onClick={() => navigate("/database")}>
                                <Search className="w-3.5 h-3.5" />
                                Rechercher mes clients
                            </Button>
                        </div>
                    ) : selection.length === 0 ? (
                        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-8">
                            <Layers className="w-8 h-8 text-ink-faint mb-2" />
                            <p className="text-sm font-semibold text-ink mb-0.5">Aucun établissement sélectionné</p>
                            <p className="text-xs text-ink-muted">
                                Cherchez-les un par un, ou importez une liste de prospection
                            </p>
                        </div>
                    ) : (
                        <div className="max-h-56 overflow-y-auto flex flex-wrap gap-1.5">
                            {selection.map(establishment => (
                                <span
                                    key={establishment._id}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-sunken px-2.5 py-1 text-xs text-ink"
                                >
                                    <span className="truncate max-w-[220px]">{establishment.name}</span>
                                    {establishment.city && (
                                        <span className="text-ink-faint truncate">{establishment.city}</span>
                                    )}
                                    <button
                                        onClick={() => removeOne(establishment._id)}
                                        className="text-ink-faint hover:text-danger transition-colors duration-150"
                                        aria-label={`Retirer ${establishment.name}`}
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center gap-3">
                        <Button onClick={analyze} disabled={analyzing || selection.length < MIN_SELECTION}>
                            {analyzing
                                ? <Loader2 className="w-4 h-4 motion-safe:animate-spin" />
                                : <Sparkles className="w-4 h-4" />}
                            {analyzing ? "Analyse en cours…" : result ? "Relancer l'analyse" : "Analyser la sélection"}
                        </Button>
                        {stale && !analyzing && !error && (
                            <p className="text-xs text-warning">
                                Sélection modifiée depuis l'analyse affichée ci-dessous.
                            </p>
                        )}
                        {error && (
                            <p className="flex items-center gap-1.5 text-xs text-danger">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                {error}
                            </p>
                        )}
                    </div>
                </Card>

                {/* ── Saved types ── */}
                <TypologyManager
                    typologies={typologies}
                    loading={typologiesLoading}
                    onChanged={reloadTypologies}
                />

                {/* ── Results ── */}
                {result && (
                    <div className="space-y-6">
                        {result.aiError && (
                            <p className="flex items-start gap-1.5 rounded-xl border border-warning/20 bg-warning-subtle p-3 text-xs text-warning">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                                {AI_ERRORS[result.aiError] ?? "L'assistant n'a pas pu produire de profils."}
                            </p>
                        )}

                        {result.summary && (
                            <Card>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint mb-2 flex items-center gap-1.5">
                                    <Sparkles className="w-3 h-3 text-accent" />
                                    Physionomie du portefeuille
                                </p>
                                <p className="text-sm text-ink leading-relaxed">{result.summary}</p>
                            </Card>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <StatCard label="Établissements" value={result.total.toLocaleString("fr-FR")} />
                            <StatCard label="Profils identifiés" value={result.profiles.length} />
                            {result.stats.highlights.slice(0, 2).map(highlight => (
                                <StatCard
                                    key={highlight.label}
                                    label={highlight.label}
                                    value={<span className="text-2xl">{highlight.value}</span>}
                                    delta={
                                        <span className="text-ink-faint text-xs">
                                            sur {highlight.coverage}/{result.total}
                                        </span>
                                    }
                                />
                            ))}
                        </div>

                        {result.profiles.length > 0 && (
                            <div className="space-y-4">
                                <h2 className="text-base font-semibold text-ink">Vos profils clients</h2>
                                {result.profiles.map((profile, i) => (
                                    <ProfileCard
                                        key={`${profile.label}-${i}`}
                                        profile={profile}
                                        rank={i}
                                        onSaved={reloadTypologies}
                                    />
                                ))}
                                {result.unassigned.length > 0 && (
                                    <p className="text-xs text-ink-faint">
                                        {result.unassigned.length} établissement{result.unassigned.length > 1 ? "s" : ""} hors
                                        profil (trop atypique{result.unassigned.length > 1 ? "s" : ""}) :{" "}
                                        {result.unassigned.slice(0, 12).map(e => e.name).join(", ")}
                                        {result.unassigned.length > 12 && ` et ${result.unassigned.length - 12} autre${result.unassigned.length - 12 > 1 ? "s" : ""}`}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="space-y-3">
                            <div>
                                <h2 className="text-base font-semibold text-ink">Répartition de la sélection</h2>
                                <p className="text-xs text-ink-muted">
                                    Part de vos {result.total} établissements sur chaque critère — comptée, pas estimée
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {result.stats.blocks.map(block => (
                                    <DistributionCard key={block.id} block={block} total={result.total} />
                                ))}
                            </div>
                        </div>

                        {result.stats.highlights.length > 0 && (
                            <div className="rounded-2xl border border-border bg-surface p-4">
                                <p className="text-xs font-semibold text-ink mb-3">Médianes</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
                                    {result.stats.highlights.map(highlight => (
                                        <div key={highlight.label} className="min-w-0">
                                            <p className="text-[10px] uppercase tracking-wide text-ink-faint truncate">
                                                {highlight.label}
                                            </p>
                                            <p className="text-sm font-medium text-ink">{highlight.value}</p>
                                            <p className="text-[10px] text-ink-faint">
                                                sur {highlight.coverage}/{result.total}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
